import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CopyButton } from './copy-button';
import { StatusWord } from './licenses-table';
import type { License } from '@/schemas/licenses';

interface LicenseDetailHeaderProps {
  serial: string;
  status: License['status'];
  onDownloadOffline: () => void;
  showDownloadOffline: boolean;
}

export function LicenseDetailHeader({
  serial,
  status,
  onDownloadOffline,
  showDownloadOffline,
}: LicenseDetailHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <Link
        to="/licenses"
        className="inline-flex items-center rounded-sm text-[13.5px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        {t('licenses.detail.back')}
      </Link>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <code className="font-mono text-[22px] font-semibold tracking-tight text-foreground">
            {serial}
          </code>
          <CopyButton
            text={serial}
            label={t('common.copySerial')}
            size="sm"
            data-testid={`copy-serial-${serial}`}
          />
          <StatusWord status={status} />
        </div>

        {showDownloadOffline && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onDownloadOffline}
              data-testid="download-offline-btn"
            >
              {t('licenses.detail.downloadOffline')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
